
import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import LanguageSwitcher from "@/components/shared/LanguageSwitcher";
import { Menu, X } from "lucide-react";

export const Navbar: React.FC = () => {
  const { t, language } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // Track scroll position
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const navItems = [
    { name: t("nav.home"), path: "/" },
    { name: t("nav.sdgs"), path: "/sdgs" },
    { name: t("nav.media"), path: "/media" },
    { name: t("nav.journey"), path: "/journey" },
  ];

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "py-2 bg-palsustain-green shadow-md backdrop-blur-sm" : "py-3 bg-palsustain-green"
      }`}
    >
      <div className="container mx-auto px-4 flex justify-between items-center">
        {/* Logo */}
        <Link 
          to="/" 
          className="relative z-50 flex items-start"
          aria-label="Palsustain - Home"
        >
          <div className="flex flex-col items-start">
            <span className="text-2xl font-bold text-white">
              Palsustain
            </span>
            <span className="text-xs text-white/90 font-light">Sustainable Development for Palestine</span>
          </div>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`px-4 py-2 mx-1 rounded-md transition-all ${
                isActive(item.path)
                  ? "text-white font-medium bg-white/10"
                  : "text-white/90 hover:text-white hover:bg-white/10"
              }`}
            >
              {item.name}
            </Link>
          ))}
          <div className="border-l border-white/20 h-6 mx-2" />
          <LanguageSwitcher />
        </nav>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden relative z-50 p-2"
          onClick={() => setIsOpen(!isOpen)}
          aria-label={isOpen ? "Close menu" : "Open menu"}
        >
          {isOpen ? (
            <X className="h-6 w-6 text-white" />
          ) : (
            <Menu className="h-6 w-6 text-white" />
          )}
        </button>

        {/* Mobile Navigation */}
        <div
          className={`fixed inset-0 bg-palsustain-green z-40 transition-transform duration-300 transform md:hidden ${
            isOpen ? "translate-x-0" : language === "ar" ? "translate-x-full" : "-translate-x-full"
          }`}
        >
          <div className="flex flex-col h-full pt-20 px-6">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`py-4 text-lg border-b border-white/10 ${
                  isActive(item.path)
                    ? "text-white font-medium"
                    : "text-white/90"
                }`}
              >
                {item.name}
              </Link>
            ))}
            <div className="mt-auto mb-8 flex justify-center py-4">
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
